/**
 * @zendir/ui - Capture Component
 *
 * Transport-agnostic image capture server component. Generates a unique server
 * ID, accepts capture requests imperatively, and returns image bytes. The host
 * app (e.g. space-range-operator) handles all MQTT/transport wiring and uses
 * the server ID to construct topic paths externally.
 *
 * Features:
 * - Unique server ID with copy-to-clipboard
 * - Pause/resume capture processing
 * - Collapsible gallery of captured images
 * - Connection status display
 * - forwardRef with imperative CaptureHandle
 *
 * @example
 * ```tsx
 * const captureRef = useRef<CaptureHandle>(null);
 *
 * <Capture
 *   ref={captureRef}
 *   connected={isConnected}
 *   onReady={(serverId) => {
 *     mqttClient.subscribe('Zendir/SpaceImage/Request');
 *   }}
 *   onCapture={(req) => console.log('Captured', req.req_id)}
 * />
 *
 * // When a request arrives from external transport:
 * // Request: { req_id, args: { game_id, team_id, ...captureArgs } }
 * const imageBytes = await captureRef.current.capture(request);
 * // Publish response to: Zendir/SpaceImage/{game_id}/{team_id}/Response/{req_id}
 * ```
 */

import React, {
  memo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTheme } from "../../theme";
import { useCopyToClipboard } from "../display/CopyButton";
import placeholderImageUrl from "./capture-placeholder.png";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Camera capture arguments */
export interface CaptureArgs {
  monochromatic: boolean;
  resolution: number;
  coc: number;
  pixel_pitch: number;
  focusing_distance: number;
  aperture: number;
  focal_length: number;
  fov: number;
  sample: boolean;
  position: [number, number, number];
  rotation: [number, number, number];
  /**
   * Output image format. Defaults to 'png' when omitted.
   * - 'png'  → lossless, larger file (good for scientific/archival use)
   * - 'jpeg' → lossy, smaller file (~3-5× smaller than PNG at q=0.92)
   */
  format?: "png" | "jpeg";
  /**
   * JPEG quality factor (0–1). Only used when format='jpeg'. Default: 0.92.
   * Ignored for PNG (which is always lossless).
   */
  jpeg_quality?: number;
}

/** Incoming capture request */
export interface CaptureRequest {
  type: "capture";
  req_id: number;
  args: CaptureArgs;
}

/** Imperative handle exposed via ref */
export interface CaptureHandle {
  /** Process a capture request, returns image bytes */
  capture: (request: CaptureRequest) => Promise<Uint8Array>;
  /** The server's unique ID */
  serverId: string;
}

/** Captured image entry stored for gallery display */
export interface CapturedImage {
  reqId: number;
  dataUrl: string;
  timestamp: number;
  /** Raw image byte count */
  bytes: number;
  /** The CaptureArgs used for this capture (camera settings, position, etc.) */
  args?: CaptureArgs;
}

export interface CaptureProps {
  /** Whether host transport is connected (drives status indicator) */
  connected?: boolean;
  /**
   * Image source for capture responses. Three tiers:
   * 1. `Uint8Array` -- static bytes returned for every request
   * 2. `(args) => Promise<Uint8Array>` -- async renderer (e.g. CesiumCaptureSource)
   * 3. `undefined` (default) -- auto-detects CesiumJS; falls back to placeholder image
   */
  imageSource?: Uint8Array | ((args: CaptureArgs) => Promise<Uint8Array>);
  /**
   * Pre-load the CesiumJS renderer on mount instead of waiting for the first
   * capture request. Only applies when `imageSource` is not provided (auto-detect mode).
   * @default true
   */
  preload?: boolean;
  /**
   * Explicit server ID. When provided the component uses this value instead
   * of generating a new UUID. Useful for persisting across reloads via localStorage.
   */
  serverId?: string;
  /** Fired after each capture is processed */
  onCapture?: (request: CaptureRequest) => void;
  /** Called once on mount with the server ID */
  onReady?: (serverId: string) => void;
  /**
   * Start with the image gallery expanded. When true, the component renders
   * in a flex-column layout designed to fill its container height.
   * @default false
   */
  defaultGalleryExpanded?: boolean;
  /** Custom style */
  style?: React.CSSProperties;
}

// ─── Placeholder Image ───────────────────────────────────────────────────────

/**
 * Generates a visible 256x256 fallback PNG via canvas when the bundled
 * astronaut image cannot be fetched (Storybook asset path, SSR, etc.).
 * Shows a branded "ZENDIR" placeholder so thumbnails are never invisible.
 */
async function generateCanvasFallback(): Promise<Uint8Array> {
  try {
    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    // Dark space-themed gradient background
    const bg = ctx.createRadialGradient(
      size / 2,
      size / 2,
      20,
      size / 2,
      size / 2,
      size,
    );
    bg.addColorStop(0, "#1e2a4a");
    bg.addColorStop(1, "#0a0e1a");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);

    // Subtle star field
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    for (let i = 0; i < 40; i++) {
      const sx = (i * 97 + 31) % size;
      const sy = (i * 61 + 17) % size;
      const sr = ((i % 3) + 1) * 0.6;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }

    // Crosshair / reticle
    ctx.strokeStyle = "rgba(74, 143, 255, 0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(size / 2, 40);
    ctx.lineTo(size / 2, size - 40);
    ctx.moveTo(40, size / 2);
    ctx.lineTo(size - 40, size / 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 50, 0, Math.PI * 2);
    ctx.stroke();

    // "ZENDIR" label
    ctx.fillStyle = "#4a8fff";
    ctx.font = "bold 28px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("ZENDIR", size / 2, size / 2 - 16);

    // "PLACEHOLDER" subtitle
    ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
    ctx.font = "13px monospace";
    ctx.fillText("PLACEHOLDER", size / 2, size / 2 + 14);

    // Res badge at bottom
    ctx.fillStyle = "rgba(255, 255, 255, 0.25)";
    ctx.font = "10px monospace";
    ctx.fillText(`${size}x${size}`, size / 2, size - 20);

    const blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), "image/png"),
    );
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    // Absolute last resort: 1x1 opaque dark-blue PNG (never transparent/black)
    return new Uint8Array([
      0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a, 0x00, 0x00, 0x00, 0x0d,
      0x49, 0x48, 0x44, 0x52, 0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01,
      0x08, 0x02, 0x00, 0x00, 0x00, 0x90, 0x77, 0x53, 0xde, 0x00, 0x00, 0x00,
      0x0c, 0x49, 0x44, 0x41, 0x54, 0x78, 0x9c, 0x62, 0x68, 0x60, 0x60, 0x00,
      0x00, 0x00, 0x04, 0x00, 0x01, 0x27, 0x34, 0x27, 0x0a, 0x00, 0x00, 0x00,
      0x00, 0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82,
    ]);
  }
}

/** Cached placeholder image bytes (lazy-loaded from bundled asset) */
let placeholderCache: Uint8Array | null = null;

/**
 * Loads the bundled placeholder image (astronaut). Falls back to a
 * canvas-generated branded placeholder if the fetch fails (e.g. SSR,
 * Storybook asset paths, missing file).
 */
async function loadPlaceholderImage(): Promise<Uint8Array> {
  if (placeholderCache) return placeholderCache;
  try {
    const response = await fetch(placeholderImageUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const buf = await response.arrayBuffer();
    if (buf.byteLength < 100) throw new Error("Suspiciously small image");
    placeholderCache = new Uint8Array(buf);
  } catch {
    placeholderCache = await generateCanvasFallback();
  }
  return placeholderCache;
}

/**
 * Bundled astronaut placeholder PNG (or 1x1 fallback). Exported for
 * `CesiumCaptureSource` when globe capture fails (e.g. missing Cesium assets, WebGL).
 */
export async function loadCapturePlaceholderImage(): Promise<Uint8Array> {
  return loadPlaceholderImage();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseCaptureOptions {
  /** Image to return: static bytes or async function receiving capture args */
  imageSource?: Uint8Array | ((args: CaptureArgs) => Promise<Uint8Array>);
  /**
   * Pre-load the CesiumJS renderer on mount (only when imageSource is undefined).
   * Set to false for lazy init on first capture request.
   * @default true
   */
  preload?: boolean;
  /**
   * Explicit server ID. When provided the hook uses this value instead of
   * generating a new `crypto.randomUUID()`. Useful for persisting the ID
   * across page reloads (e.g. from localStorage).
   */
  serverId?: string;
}

export interface UseCaptureResult {
  /** Stable unique server ID */
  serverId: string;
  /** Process a capture request, returns image bytes (empty if paused) */
  capture: (request: CaptureRequest) => Promise<Uint8Array>;
  /** Whether capture processing is paused */
  paused: boolean;
  /** Toggle pause state */
  setPaused: (paused: boolean) => void;
  /** Number of successfully processed requests */
  requestCount: number;
  /** Most recent request */
  lastRequest: CaptureRequest | null;
  /** Gallery of captured images (newest first) */
  capturedImages: CapturedImage[];
}

/**
 * Hook for capture server logic. Generates a stable UUID, manages
 * pause state, and processes capture requests into image bytes.
 *
 * @example
 * ```tsx
 * const { serverId, capture, paused, setPaused } = useCapture();
 * // call capture(request) when external message arrives
 * ```
 */
export function useCapture(options?: UseCaptureOptions): UseCaptureResult {
  const serverIdRef = useRef(options?.serverId || crypto.randomUUID());
  const [paused, setPaused] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [lastRequest, setLastRequest] = useState<CaptureRequest | null>(null);
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);

  const imageSourceRef = useRef(options?.imageSource);
  imageSourceRef.current = options?.imageSource;

  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // Cache generated images by req_id to avoid regenerating the same capture
  const imageCacheRef = useRef<Map<number, Uint8Array>>(new Map());

  // Auto-detect CesiumJS when no imageSource is provided.
  // Lazily creates a hidden Cesium.Viewer on first use (or on mount if preload=true).
  const cesiumSourceRef = useRef<{
    capture: (args: CaptureArgs) => Promise<Uint8Array>;
    destroy: () => void;
  } | null>(null);
  const cesiumFailedRef = useRef(false);
  const cesiumLoadingRef = useRef<Promise<void> | null>(null);

  const initCesium = useCallback(async () => {
    if (cesiumSourceRef.current || cesiumFailedRef.current) return;
    try {
      const { createCesiumCaptureSource } =
        await import("../../3d/CesiumCaptureSource");
      cesiumSourceRef.current = await createCesiumCaptureSource();
    } catch {
      cesiumFailedRef.current = true;
    }
  }, []);

  // Pre-load Cesium on mount when preload is enabled and no explicit imageSource
  const preload = options?.preload !== false;
  useEffect(() => {
    if (imageSourceRef.current || !preload) return;
    cesiumLoadingRef.current = initCesium();
  }, [preload, initCesium]);

  // Cleanup Cesium viewer on unmount
  useEffect(() => {
    return () => {
      cesiumSourceRef.current?.destroy();
      cesiumSourceRef.current = null;
    };
  }, []);

  const capture = useCallback(
    async (request: CaptureRequest): Promise<Uint8Array> => {
      if (pausedRef.current) return new Uint8Array(0);

      if (
        !request ||
        request.type !== "capture" ||
        typeof request.req_id !== "number"
      ) {
        return new Uint8Array(0);
      }

      // Return cached image if this req_id was already processed
      const cached = imageCacheRef.current.get(request.req_id);
      if (cached) return cached;

      let bytes: Uint8Array;
      const src = imageSourceRef.current;

      if (src instanceof Uint8Array) {
        bytes = src;
      } else if (typeof src === "function") {
        bytes = await src(request.args);
      } else {
        // Auto-detect: try CesiumJS, fall back to placeholder
        if (!cesiumSourceRef.current && !cesiumFailedRef.current) {
          if (cesiumLoadingRef.current) {
            await cesiumLoadingRef.current;
          } else {
            await initCesium();
          }
        }
        if (cesiumSourceRef.current) {
          bytes = await cesiumSourceRef.current.capture(request.args);
        } else {
          bytes = await loadPlaceholderImage();
        }
      }

      // Cache the result by req_id
      imageCacheRef.current.set(request.req_id, bytes);

      setRequestCount((c) => c + 1);
      setLastRequest(request);

      const mimeType =
        request.args.format === "jpeg" ? "image/jpeg" : "image/png";
      const blob = new Blob([bytes as unknown as BlobPart], { type: mimeType });
      const dataUrl = URL.createObjectURL(blob);
      setCapturedImages((prev) => [
        {
          reqId: request.req_id,
          dataUrl,
          timestamp: Date.now(),
          bytes: bytes.length,
          args: request.args,
        },
        ...prev,
      ]);

      return bytes;
    },
    [],
  );

  return {
    serverId: serverIdRef.current,
    capture,
    paused,
    setPaused,
    requestCount,
    lastRequest,
    capturedImages,
  };
}

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────

const PauseIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const PlayIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6,4 20,12 6,20" />
  </svg>
);

const ChevronIcon = ({
  size = 14,
  expanded,
}: {
  size?: number;
  expanded: boolean;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: "transform 0.2s ease",
      transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CopyIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = ({ size = 14, color }: { size?: number; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Capture server UI component. Displays server status, pause/resume control,
 * copy-ID button, and a collapsible gallery of captured images.
 *
 * Access `capture()` and `serverId` imperatively via the forwarded ref.
 */
export const Capture = memo(
  forwardRef<CaptureHandle, CaptureProps>(function Capture(
    {
      connected = false,
      imageSource,
      preload,
      serverId: serverIdProp,
      onCapture,
      onReady,
      defaultGalleryExpanded = false,
      style,
    },
    ref,
  ) {
    const { tokens } = useTheme();
    const {
      serverId,
      capture,
      paused,
      setPaused,
      requestCount,
      capturedImages,
    } = useCapture({ imageSource, preload, serverId: serverIdProp });
    const { copy, copied } = useCopyToClipboard();
    const [galleryExpanded, setGalleryExpanded] = useState(
      defaultGalleryExpanded,
    );
    const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
    const readyFiredRef = useRef(false);

    useEffect(() => {
      if (onReady && !readyFiredRef.current) {
        readyFiredRef.current = true;
        onReady(serverId);
      }
    }, [onReady, serverId]);

    const wrappedCapture = useCallback(
      async (request: CaptureRequest) => {
        const bytes = await capture(request);
        if (bytes.length > 0) {
          onCapture?.(request);
        }
        return bytes;
      },
      [capture, onCapture],
    );

    useImperativeHandle(
      ref,
      () => ({
        capture: wrappedCapture,
        serverId,
      }),
      [wrappedCapture, serverId],
    );

    const statusColor = connected
      ? tokens.colors.status.normal
      : tokens.colors.status.off;
    const statusLabel = connected ? "Connected" : "Disconnected";

    const btnBase: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      height: 32,
      padding: "0 12px",
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily.primary,
      color: tokens.colors.text.secondary,
      backgroundColor: "transparent",
      border: `1px solid ${tokens.colors.border.muted}`,
      borderRadius: tokens.borderRadius.md,
      cursor: "pointer",
      transition: tokens.animation.fast,
      outline: "none",
      whiteSpace: "nowrap",
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          borderRadius: tokens.borderRadius.lg,
          border: `1px solid ${tokens.colors.border.default}`,
          backgroundColor: tokens.colors.background.surface,
          overflow: "hidden",
          /* When gallery is expanded, allow container to stretch to fill parent */
          ...(galleryExpanded ? { flex: 1, minHeight: 0 } : {}),
          ...style,
        }}
      >
        {/* Top bar: pause/resume | status | copy ID */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            gap: "12px",
            minHeight: 48,
            flexShrink: 0,
          }}
        >
          {/* Left: Pause/Resume */}
          <button
            type="button"
            onClick={() => setPaused(!paused)}
            onMouseEnter={() => setHoveredBtn("pause")}
            onMouseLeave={() => setHoveredBtn(null)}
            aria-label={
              paused ? "Resume capture server" : "Pause capture server"
            }
            style={{
              ...btnBase,
              color: paused
                ? tokens.colors.status.caution
                : tokens.colors.text.secondary,
              borderColor: paused
                ? `${tokens.colors.status.caution}40`
                : tokens.colors.border.muted,
              backgroundColor:
                hoveredBtn === "pause"
                  ? `${tokens.colors.accent.primary}12`
                  : "transparent",
            }}
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
            <span>{paused ? "Resume" : "Pause"}</span>
          </button>

          {/* Center: Status + request count */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.secondary,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusColor,
                flexShrink: 0,
              }}
            />
            <span>{statusLabel}</span>
            {requestCount > 0 && (
              <span
                style={{
                  fontSize: tokens.typography.fontSize.xs,
                  color: tokens.colors.text.tertiary,
                  fontFamily: tokens.typography.fontFamily.mono,
                }}
              >
                ({requestCount})
              </span>
            )}
          </div>

          {/* Right: Copy ID */}
          <button
            type="button"
            onClick={() => copy(serverId)}
            onMouseEnter={() => setHoveredBtn("copy")}
            onMouseLeave={() => setHoveredBtn(null)}
            title={copied ? "Copied!" : `Copy Server ID: ${serverId}`}
            aria-label={copied ? "Server ID copied" : "Copy server ID"}
            style={{
              ...btnBase,
              color: copied
                ? tokens.colors.status.normal
                : tokens.colors.text.secondary,
              borderColor: copied
                ? `${tokens.colors.status.normal}40`
                : tokens.colors.border.muted,
              backgroundColor:
                hoveredBtn === "copy"
                  ? `${tokens.colors.accent.primary}12`
                  : "transparent",
            }}
          >
            {copied ? (
              <CheckIcon color={tokens.colors.status.normal} />
            ) : (
              <CopyIcon />
            )}
            <span
              style={{
                fontFamily: tokens.typography.fontFamily.mono,
                fontSize: tokens.typography.fontSize.xs,
              }}
            >
              {copied ? "Copied!" : serverId.slice(0, 8)}
            </span>
          </button>
        </div>

        {/* Captured Images gallery (respects defaultGalleryExpanded prop) */}
        <CaptureGallery
          images={capturedImages}
          expanded={galleryExpanded}
          onToggle={() => setGalleryExpanded(!galleryExpanded)}
          tokens={tokens}
          serverId={serverId}
        />
      </div>
    );
  }),
);

// ─── Gallery Sub-Components ───────────────────────────────────────────────────

/** Format byte count for display */
function fmtBytes(b: number): string {
  if (b > 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b > 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${b} B`;
}

/** Download a single captured image with the correct file extension */
function downloadCapturedImage(img: CapturedImage, serverId: string): void {
  const ext = img.args?.format === "jpeg" ? "jpg" : "png";
  const a = document.createElement("a");
  a.href = img.dataUrl;
  a.download = `capture_${serverId}_${img.reqId}.${ext}`;
  a.click();
}

/** Download all captured images sequentially */
async function downloadAllImages(
  images: CapturedImage[],
  serverId: string,
): Promise<void> {
  for (const img of images) {
    downloadCapturedImage(img, serverId);
    await new Promise<void>((r) => setTimeout(r, 200));
  }
}

/** Download icon (inline SVG) */
const DownloadIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/** Close icon (inline SVG) */
const CloseIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/** Metadata key-value row */
function MetaRow({
  label,
  value,
  tokens,
  mono,
}: {
  label: string;
  value: string;
  tokens: any;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "2px 0",
        fontSize: tokens.typography.fontSize.xs,
      }}
    >
      <span style={{ color: tokens.colors.text.tertiary }}>{label}</span>
      <span
        style={{
          color: tokens.colors.text.primary,
          fontFamily: mono ? tokens.typography.fontFamily.mono : "inherit",
          fontSize: mono ? "0.68rem" : tokens.typography.fontSize.xs,
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** Metadata group heading */
function MetaGroup({
  label,
  tokens,
  children,
}: {
  label: string;
  tokens: any;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 600,
          color: tokens.colors.text.tertiary,
          textTransform: "uppercase" as const,
          letterSpacing: "0.06em",
          padding: "4px 0 2px",
          borderTop: `1px solid ${tokens.colors.border.muted}`,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * Image detail panel showing preview, metadata, camera settings, and position.
 * Displayed inline when a gallery thumbnail is clicked.
 */
function CaptureImageDetail({
  img,
  tokens,
  serverId,
  onClose,
}: {
  img: CapturedImage;
  tokens: any;
  serverId: string;
  onClose: () => void;
}) {
  const a = img.args;
  const time = new Date(img.timestamp).toLocaleString("en-US", {
    hour12: false,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      style={{
        backgroundColor: tokens.colors.background.elevated,
        display: "flex",
        gap: "8px",
        padding: "8px",
        minHeight: 0,
      }}
    >
      {/* Left: Image preview + actions */}
      <div
        style={{
          flex: "1 1 50%",
          display: "flex",
          flexDirection: "column" as const,
          gap: "6px",
          minWidth: 0,
        }}
      >
        <img
          src={img.dataUrl}
          alt={`capture_${serverId}_${img.reqId}`}
          style={{
            width: "100%",
            borderRadius: tokens.borderRadius.sm,
            border: `1px solid ${tokens.colors.border.muted}`,
            objectFit: "contain" as const,
          }}
        />
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            type="button"
            onClick={() => downloadCapturedImage(img, serverId)}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "5px 0",
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: 500,
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.accent.primary,
              backgroundColor: `${tokens.colors.accent.primary}12`,
              border: `1px solid ${tokens.colors.accent.primary}30`,
              cursor: "pointer",
            }}
          >
            <DownloadIcon /> Download PNG
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "5px 8px",
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.tertiary,
              backgroundColor: "transparent",
              border: `1px solid ${tokens.colors.border.muted}`,
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Right: Metadata */}
      <div
        style={{
          flex: "1 1 50%",
          overflowY: "auto" as const,
          minWidth: 0,
        }}
      >
        <MetaGroup label="File Info" tokens={tokens}>
          <MetaRow
            label="Filename"
            value={`capture_${serverId}_${img.reqId}.${img.args?.format === "jpeg" ? "jpg" : "png"}`}
            tokens={tokens}
            mono
          />
          <MetaRow
            label="Req ID"
            value={`#${img.reqId}`}
            tokens={tokens}
            mono
          />
          <MetaRow label="Size" value={fmtBytes(img.bytes)} tokens={tokens} />
          <MetaRow
            label="Format"
            value={
              img.args?.format === "jpeg"
                ? `JPEG (q=${Math.round((img.args.jpeg_quality ?? 0.92) * 100)}%)`
                : "PNG"
            }
            tokens={tokens}
          />
          <MetaRow label="Captured" value={time} tokens={tokens} />
        </MetaGroup>

        {/* Camera settings (only if args are available) */}
        {a && (
          <>
            <MetaGroup label="Camera" tokens={tokens}>
              <MetaRow
                label="Resolution"
                value={`${a.resolution}px`}
                tokens={tokens}
              />
              <MetaRow label="FOV" value={`${a.fov}°`} tokens={tokens} />
              <MetaRow
                label="Focal Length"
                value={`${a.focal_length}mm`}
                tokens={tokens}
              />
              <MetaRow
                label="Aperture"
                value={`f/${a.aperture}`}
                tokens={tokens}
              />
              <MetaRow label="CoC" value={`${a.coc}mm`} tokens={tokens} />
              <MetaRow
                label="Pixel Pitch"
                value={`${a.pixel_pitch}mm`}
                tokens={tokens}
              />
              <MetaRow
                label="Focus Dist"
                value={`${a.focusing_distance}m`}
                tokens={tokens}
              />
              <MetaRow
                label="Monochrome"
                value={a.monochromatic ? "Yes" : "No"}
                tokens={tokens}
              />
              <MetaRow
                label="Sample"
                value={a.sample ? "Yes (½ res)" : "No"}
                tokens={tokens}
              />
            </MetaGroup>

            <MetaGroup label="Position & Orientation" tokens={tokens}>
              <MetaRow
                label="X"
                value={`${a.position[0].toFixed(2)} km`}
                tokens={tokens}
                mono
              />
              <MetaRow
                label="Y"
                value={`${a.position[1].toFixed(2)} km`}
                tokens={tokens}
                mono
              />
              <MetaRow
                label="Z"
                value={`${a.position[2].toFixed(2)} km`}
                tokens={tokens}
                mono
              />
              <MetaRow
                label="Heading"
                value={`${a.rotation[0].toFixed(1)}°`}
                tokens={tokens}
                mono
              />
              <MetaRow
                label="Pitch"
                value={`${a.rotation[1].toFixed(1)}°`}
                tokens={tokens}
                mono
              />
              <MetaRow
                label="Roll"
                value={`${a.rotation[2].toFixed(1)}°`}
                tokens={tokens}
                mono
              />
            </MetaGroup>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Collapsible image gallery with thumbnails, detail panel, download per-image, and download-all.
 * Part of the Capture component's built-in UI.
 */
function CaptureGallery({
  images,
  expanded,
  onToggle,
  tokens,
  serverId,
}: {
  images: CapturedImage[];
  expanded: boolean;
  onToggle: () => void;
  tokens: any;
  serverId: string;
}) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const selectedImg = selectedIdx !== null ? images[selectedIdx] : null;

  return (
    <div
      style={{
        borderTop: `1px solid ${tokens.colors.border.muted}`,
        /* Fill remaining space when expanded */
        ...(expanded
          ? {
              flex: 1,
              display: "flex",
              flexDirection: "column" as const,
              minHeight: 0,
              overflow: "hidden",
            }
          : {}),
      }}
    >
      {/* Header row: toggle + count + download-all */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`Captured images, ${images.length} total`}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: 500,
            fontFamily: tokens.typography.fontFamily.primary,
            color: tokens.colors.text.secondary,
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            outline: "none",
            textAlign: "left" as const,
          }}
        >
          <ChevronIcon expanded={expanded} />
          <span>Captured Images</span>
          {images.length > 0 && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
                fontFamily: tokens.typography.fontFamily.mono,
              }}
            >
              ({images.length})
            </span>
          )}
        </button>

        {/* Download All button (visible when expanded and has images) */}
        {expanded && images.length > 0 && (
          <button
            type="button"
            onClick={() => downloadAllImages(images, serverId)}
            title={`Download all ${images.length} images`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 8px",
              marginRight: 8,
              fontSize: "0.62rem",
              fontWeight: 500,
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.tertiary,
              backgroundColor: "transparent",
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.sm,
              cursor: "pointer",
              whiteSpace: "nowrap" as const,
            }}
          >
            <DownloadIcon size={10} /> All
          </button>
        )}
      </div>

      {expanded && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            overflow: "hidden",
          }}
        >
          {images.length === 0 ? (
            <div
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                padding: "12px",
                textAlign: "center" as const,
                width: "100%",
              }}
            >
              No images captured yet
            </div>
          ) : (
            <>
              {/* Left: scrollable thumbnail grid
                 - Detail panel open (200px strip): 2 columns fixed
                 - Full width: 4 columns on desktop, collapses via auto-fill on narrow viewports */}
              <div
                style={{
                  width: selectedImg ? 200 : "100%",
                  flexShrink: 0,
                  overflowY: "auto" as const,
                  padding: "6px",
                  borderRight: selectedImg
                    ? `1px solid ${tokens.colors.border.muted}`
                    : "none",
                  display: "grid",
                  gridTemplateColumns: selectedImg
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fill, minmax(min(120px, 100%), 1fr))",
                  gridAutoRows: "min-content",
                  gap: "4px",
                  alignContent: "start" as const,
                }}
              >
                {images.map((img, idx) => {
                  const isSelected = selectedIdx === idx;
                  return (
                    <div
                      key={`${img.reqId}-${img.timestamp}`}
                      onClick={() => setSelectedIdx(isSelected ? null : idx)}
                      style={{
                        position: "relative" as const,
                        aspectRatio: "1",
                        borderRadius: tokens.borderRadius.sm,
                        overflow: "hidden",
                        border: `2px solid ${isSelected ? tokens.colors.accent.primary : tokens.colors.border.muted}`,
                        backgroundColor: tokens.colors.background.elevated,
                        cursor: "pointer",
                        transition: "border-color 0.15s",
                      }}
                    >
                      <img
                        src={img.dataUrl}
                        alt={`capture_${serverId}_${img.reqId}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover" as const,
                          display: "block",
                        }}
                      />
                      {/* Bottom label: #reqId + size */}
                      <div
                        style={{
                          position: "absolute" as const,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "2px 4px",
                          fontSize: "9px",
                          fontFamily: tokens.typography.fontFamily.mono,
                          color: "#fff",
                          backgroundColor: "rgba(0, 0, 0, 0.65)",
                        }}
                      >
                        <span>#{img.reqId}</span>
                        <span>{fmtBytes(img.bytes)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right: detail panel for selected image */}
              {selectedImg && (
                <div
                  style={{ flex: 1, minWidth: 0, overflowY: "auto" as const }}
                >
                  <CaptureImageDetail
                    img={selectedImg}
                    tokens={tokens}
                    serverId={serverId}
                    onClose={() => setSelectedIdx(null)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Capture;
